import Controller from "./application_controller"

/**
 * Visually formats a text input's value with thousands separators as the
 * user types. When submitted through a form, the separators are stripped
 * on the form's `formdata` event so the server receives a raw number.
 *
 *   <input type="text" inputmode="decimal"
 *          data-controller="number-format"
 *          data-number-format-thousands-value=","
 *          data-number-format-decimal-value=".">
 */
export default class extends Controller {
  static values = {
    thousands: { type: String, default: "," },
    decimal:   { type: String, default: "." }
  }

  connect() {
    queueMicrotask(() => this.format(false))
    this.addEventListener("input", () => this.format(true))
    this.addEventListener(document, "formdata", event => {
      if (event.target.contains(this.element)) this.normalizeFormData(event)
    })
  }

  format(preserveCursor) {
    const input = this.element
    const before = input.value
    const cursor = input.selectionStart ?? before.length
    const targetTokens = this.countTokens(before.slice(0, cursor))

    const formatted = this.formatValue(before)
    if (formatted === before) return

    input.value = formatted
    if (!preserveCursor) return

    let count = 0
    let newCursor = formatted.length
    for (let i = 0; i < formatted.length; i++) {
      if (count === targetTokens) { newCursor = i; break }
      if (this.isToken(formatted[i])) count++
    }
    input.setSelectionRange(newCursor, newCursor)
  }

  normalizeFormData(event) {
    const name = this.element.name
    if (!name) return
    event.formData.set(name, this.rawValue)
  }

  get rawValue() {
    let value = this.element.value.split(this.thousandsValue).join("")
    if (this.decimalValue !== ".") value = value.replace(this.decimalValue, ".")
    return value
  }

  formatValue(raw) {
    const dec  = this.decimalValue
    const thou = this.thousandsValue

    let sign = ""
    if (raw.startsWith("-")) { sign = "-"; raw = raw.slice(1) }

    raw = raw.replace(new RegExp(`[^\\d${this.escape(dec)}]`, "g"), "")

    const firstDec = raw.indexOf(dec)
    if (firstDec !== -1) {
      raw = raw.slice(0, firstDec + 1) + raw.slice(firstDec + 1).split(dec).join("")
    }

    let intPart = raw
    let decPart
    if (firstDec !== -1) [intPart, decPart] = raw.split(dec)

    if (intPart.length > 1) intPart = intPart.replace(/^0+(?=\d)/, "")

    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thou)
    return sign + formattedInt + (decPart !== undefined ? dec + decPart : "")
  }

  isToken(ch)    { return /\d/.test(ch) || ch === this.decimalValue }
  countTokens(s) { let n = 0; for (const ch of s) if (this.isToken(ch)) n++; return n }
  escape(ch)     { return ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") }
}
